/**
 * 霓虹蛇境 (Neon Snake Realm) - Canvas 渲染器模块
 *
 * 本模块提供一组纯函数，用于在 HTML5 Canvas 上绘制游戏的所有视觉元素。
 * 所有渲染函数均接收 CanvasRenderingContext2D 和相关数据作为参数，
 * 不依赖任何外部可变状态，确保渲染逻辑的可测试性和可预测性。
 *
 * 渲染顺序（由 renderGame 编排）：
 * 1. 清空画布
 * 2. 绘制背景网格
 * 3. 绘制墙壁霓虹边框
 * 4. 绘制食物（含脉冲动画）
 * 5. 绘制道具（含浮动和旋转动画）
 * 6. 绘制蛇身（渐变色彩 + 发光效果）
 * 7. 绘制粒子特效
 */

import type {
  Food,
  GridConfig,
  Obstacle,
  Particle,
  PowerUpItem,
  Snake,
} from '@/types/game';
import { PowerUpType } from '@/types/game';
import { COLOR_SCHEME } from '@/game/constants';
import { getSnakeGradientColors } from '@/game/particles';

// ============================================================================
// 内部工具函数
// ============================================================================

/**
 * 将网格坐标转换为画布像素坐标（单元格中心点）。
 *
 * 所有游戏实体的位置以网格坐标存储，渲染时需转换为像素坐标。
 * 偏移 cellSize/2 确保绘制在单元格中心。
 *
 * @param gridX - 网格 X 坐标
 * @param gridY - 网格 Y 坐标
 * @param cellSize - 单元格像素大小
 * @returns 包含像素坐标的对象 { px, py }
 */
function gridToPixel(
  gridX: number,
  gridY: number,
  cellSize: number
): { px: number; py: number } {
  return {
    px: gridX * cellSize + cellSize / 2,
    py: gridY * cellSize + cellSize / 2,
  };
}

/**
 * 在两个颜色之间进行线性插值。
 *
 * 用于蛇身渐变色渲染，根据节段在蛇身中的位置计算中间颜色。
 * 输入和输出均为 CSS 十六进制颜色字符串。
 *
 * @param color1 - 起始颜色（如 '#00ff88'）
 * @param color2 - 结束颜色（如 '#00ccff'）
 * @param t - 插值因子 [0, 1]，0 返回 color1，1 返回 color2
 * @returns 插值后的 CSS 十六进制颜色字符串
 */
function lerpColor(color1: string, color2: string, t: number): string {
  // 将十六进制颜色解析为 RGB 分量
  const r1 = Number.parseInt(color1.slice(1, 3), 16);
  const g1 = Number.parseInt(color1.slice(3, 5), 16);
  const b1 = Number.parseInt(color1.slice(5, 7), 16);

  const r2 = Number.parseInt(color2.slice(1, 3), 16);
  const g2 = Number.parseInt(color2.slice(3, 5), 16);
  const b2 = Number.parseInt(color2.slice(5, 7), 16);

  // 线性插值
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  // 格式化为十六进制字符串
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ============================================================================
// 1. 清空画布
// ============================================================================

/**
 * 清空整个画布，使用深色太空蓝背景填充。
 *
 * 此函数应在每帧渲染开始时首先调用，确保上一帧的内容被完全清除。
 * 使用 clearRect 清除整个画布区域，然后用 fillRect 填充背景色。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param width - 画布宽度（像素）
 * @param height - 画布高度（像素）
 */
export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  // 清除画布内容
  ctx.clearRect(0, 0, width, height);

  // 填充背景色
  ctx.fillStyle = COLOR_SCHEME.background;
  ctx.fillRect(0, 0, width, height);
}

// ============================================================================
// 2. 背景网格绘制
// ============================================================================

/**
 * 绘制微弱的霓虹网格线。
 *
 * 网格线使用极低透明度的青色，营造科技感背景。
 * 每个单元格绘制水平和垂直两条线，形成完整网格。
 * 使用 save/restore 确保不影响后续绘制。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param grid - 网格配置（宽度、高度）
 * @param cellSize - 单元格像素大小
 */
export function drawGrid(
  ctx: CanvasRenderingContext2D,
  grid: GridConfig,
  cellSize: number
): void {
  const { width, height } = grid;
  const totalWidth = width * cellSize;
  const totalHeight = height * cellSize;

  ctx.save();

  // 设置网格线样式
  ctx.strokeStyle = COLOR_SCHEME.gridLine;
  ctx.lineWidth = 0.5;
  // 微弱发光效果
  ctx.shadowColor = 'rgba(0, 255, 255, 0.05)';
  ctx.shadowBlur = 2;

  // 绘制垂直线
  for (let col = 0; col <= width; col++) {
    const x = col * cellSize;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, totalHeight);
    ctx.stroke();
  }

  // 绘制水平线
  for (let row = 0; row <= height; row++) {
    const y = row * cellSize;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(totalWidth, y);
    ctx.stroke();
  }

  ctx.restore();
}

// ============================================================================
// 3. 墙壁霓虹边框绘制
// ============================================================================

/**
 * 绘制霓虹灯管风格的墙壁边框，颜色随时间缓慢循环变化。
 *
 * 边框颜色在青色 (#00ffff) 和品红 (#ff00ff) 之间平滑过渡，
 * 使用正弦波驱动颜色循环，周期约 8 秒。
 * 边框使用双层绘制：外层粗线（发光扩散） + 内层细线（核心亮线）。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param grid - 网格配置
 * @param cellSize - 单元格像素大小
 * @param time - 当前时间戳（毫秒），用于驱动颜色循环动画
 */
export function drawWallBorder(
  ctx: CanvasRenderingContext2D,
  grid: GridConfig,
  cellSize: number,
  time: number
): void {
  const totalWidth = grid.width * cellSize;
  const totalHeight = grid.height * cellSize;

  // 颜色循环：周期 8000 毫秒，使用正弦波在两种颜色间插值
  const cycleProgress = (Math.sin((time / 8000) * Math.PI * 2) + 1) / 2; // 映射到 [0, 1]
  const borderColor = lerpColor(
    COLOR_SCHEME.wallBorder[0],
    COLOR_SCHEME.wallBorder[1],
    cycleProgress
  );

  ctx.save();

  // --- 外层发光 ---
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 3;
  ctx.shadowColor = borderColor;
  ctx.shadowBlur = 12;
  ctx.strokeRect(1.5, 1.5, totalWidth - 3, totalHeight - 3);

  // --- 内层核心亮线 ---
  ctx.shadowBlur = 4;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.7;
  ctx.strokeRect(3, 3, totalWidth - 6, totalHeight - 6);

  ctx.restore();
}

// ============================================================================
// 4. 食物绘制
// ============================================================================

/**
 * 绘制普通食物：带脉冲缩放动画的发光圆球。
 *
 * 动画参数（遵循视觉设计规范）：
 * - 脉冲缩放：1.0 → 1.3 → 1.0，使用正弦波驱动
 * - 脉冲周期：1200 毫秒
 * - 缓入缓出效果：正弦波自然提供
 *
 * 食物使用双层绘制：外层发光光晕 + 内层实心圆。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param food - 食物实体
 * @param cellSize - 单元格像素大小
 * @param time - 当前时间戳（毫秒），用于驱动脉冲动画
 */
export function drawFood(
  ctx: CanvasRenderingContext2D,
  food: Food,
  cellSize: number,
  time: number
): void {
  const { px, py } = gridToPixel(food.position.x, food.position.y, cellSize);

  // 脉冲缩放计算：正弦波周期 1200ms，振幅 0.15（对应 1.0 ~ 1.3）
  const pulseScale = 1.0 + Math.sin((time / 1200) * Math.PI * 2) * 0.15;
  const baseRadius = cellSize * 0.35;
  const radius = baseRadius * pulseScale;

  ctx.save();

  // --- 外层发光光晕 ---
  ctx.shadowColor = COLOR_SCHEME.food;
  ctx.shadowBlur = 15;

  // 径向渐变光晕
  const glowGradient = ctx.createRadialGradient(px, py, radius * 0.5, px, py, radius * 2);
  glowGradient.addColorStop(0, COLOR_SCHEME.food);
  glowGradient.addColorStop(1, 'transparent');
  ctx.fillStyle = glowGradient;
  ctx.beginPath();
  ctx.arc(px, py, radius * 2, 0, Math.PI * 2);
  ctx.fill();

  // --- 内层实心圆 ---
  ctx.shadowBlur = 8;
  ctx.fillStyle = COLOR_SCHEME.food;
  ctx.beginPath();
  ctx.arc(px, py, radius, 0, Math.PI * 2);
  ctx.fill();

  // --- 高光点（增加立体感） ---
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.beginPath();
  ctx.arc(px - radius * 0.25, py - radius * 0.25, radius * 0.3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// ============================================================================
// 5. 道具绘制
// ============================================================================

/**
 * 绘制特殊道具：带浮动和旋转动画的发光图标。
 *
 * 动画参数（遵循视觉设计规范）：
 * - 垂直浮动：偏移 ±6px，正弦波驱动，周期 2000 毫秒
 * - 旋转：360 度线性旋转，周期 4000 毫秒
 *
 * 每种道具类型具有独特的图标：
 * - Speed：闪电形状
 * - Slow：龟壳/蜗牛形状
 * - WallPass：盾牌形状
 * - DoubleScore：'2x' 文字
 * - Shrink：向下箭头
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param powerUpItem - 道具实体（可为 null，不绘制）
 * @param cellSize - 单元格像素大小
 * @param time - 当前时间戳（毫秒），用于驱动动画
 */
export function drawPowerUp(
  ctx: CanvasRenderingContext2D,
  powerUpItem: PowerUpItem | null,
  cellSize: number,
  time: number
): void {
  // 如果无道具则不绘制
  if (powerUpItem === null) {
    return;
  }

  const { px, py: basePy } = gridToPixel(
    powerUpItem.position.x,
    powerUpItem.position.y,
    cellSize
  );

  // 浮动动画：正弦波，周期 2000ms，振幅 6px（缩放至 cellSize 比例）
  const scaleFactor = cellSize / 30; // 基于默认 cellSize ≈ 30 的比例
  const floatAmplitude = 6 * scaleFactor;
  const floatOffset = Math.sin((time / 2000) * Math.PI * 2) * floatAmplitude;
  const py = basePy + floatOffset;

  // 旋转动画：线性旋转，周期 4000ms
  const rotation = ((time % 4000) / 4000) * Math.PI * 2;

  // 道具颜色
  const powerUpColor =
    COLOR_SCHEME.powerUpColors[powerUpItem.type] || '#ffffff';

  const radius = cellSize * 0.4;

  ctx.save();

  // 移动到道具中心并应用旋转
  ctx.translate(px, py);
  ctx.rotate(rotation);

  // --- 外层发光光晕 ---
  ctx.shadowColor = powerUpColor;
  ctx.shadowBlur = 12;

  // 径向渐变光晕
  const glowGradient = ctx.createRadialGradient(0, 0, radius * 0.3, 0, 0, radius * 1.8);
  glowGradient.addColorStop(0, powerUpColor);
  glowGradient.addColorStop(1, 'transparent');
  ctx.fillStyle = glowGradient;
  ctx.beginPath();
  ctx.arc(0, 0, radius * 1.8, 0, Math.PI * 2);
  ctx.fill();

  // --- 实心背景圆 ---
  ctx.shadowBlur = 6;
  ctx.fillStyle = 'rgba(10, 10, 26, 0.85)';
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.fill();

  // --- 边框圆 ---
  ctx.strokeStyle = powerUpColor;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // --- 道具图标 ---
  ctx.shadowBlur = 0;
  ctx.fillStyle = powerUpColor;
  ctx.strokeStyle = powerUpColor;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const iconSize = radius * 0.55;

  switch (powerUpItem.type) {
    case PowerUpType.Speed:
      drawLightningBolt(ctx, iconSize);
      break;
    case PowerUpType.Slow:
      drawSnail(ctx, iconSize);
      break;
    case PowerUpType.WallPass:
      drawShield(ctx, iconSize);
      break;
    case PowerUpType.DoubleScore:
      drawDoubleScoreText(ctx, iconSize);
      break;
    case PowerUpType.Shrink:
      drawDownArrow(ctx, iconSize);
      break;
  }

  ctx.restore();
}

/**
 * 绘制闪电图标（加速药水）。
 */
function drawLightningBolt(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.beginPath();
  ctx.moveTo(size * 0.15, -size * 0.6);
  ctx.lineTo(-size * 0.2, size * 0.05);
  ctx.lineTo(size * 0.05, size * 0.05);
  ctx.lineTo(-size * 0.1, size * 0.7);
  ctx.lineTo(size * 0.55, -size * 0.1);
  ctx.lineTo(size * 0.15, -size * 0.1);
  ctx.closePath();
  ctx.fill();
}

/**
 * 绘制蜗牛/龟壳图标（减速药水）。
 */
function drawSnail(ctx: CanvasRenderingContext2D, size: number): void {
  // 龟壳主体
  ctx.beginPath();
  ctx.arc(0, size * 0.1, size * 0.5, Math.PI, 0);
  ctx.fill();

  // 螺旋纹
  ctx.strokeStyle = 'rgba(10, 10, 26, 0.6)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(size * 0.05, size * 0.05, size * 0.25, 0, Math.PI * 1.5);
  ctx.stroke();

  // 头部
  ctx.fillStyle = ctx.fillStyle; // 保持颜色
  ctx.beginPath();
  ctx.arc(size * 0.55, -size * 0.15, size * 0.2, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * 绘制盾牌图标（穿墙护盾）。
 */
function drawShield(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.beginPath();
  // 盾牌形状：底部尖角，顶部弧形
  ctx.moveTo(0, -size * 0.7);
  ctx.lineTo(size * 0.55, -size * 0.4);
  ctx.lineTo(size * 0.55, size * 0.15);
  ctx.quadraticCurveTo(size * 0.55, size * 0.7, 0, size * 0.75);
  ctx.quadraticCurveTo(-size * 0.55, size * 0.7, -size * 0.55, size * 0.15);
  ctx.lineTo(-size * 0.55, -size * 0.4);
  ctx.closePath();
  ctx.fill();

  // 盾牌内部高亮标记
  ctx.fillStyle = 'rgba(10, 10, 26, 0.6)';
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.35);
  ctx.lineTo(size * 0.2, -size * 0.1);
  ctx.lineTo(size * 0.2, size * 0.15);
  ctx.quadraticCurveTo(size * 0.2, size * 0.35, 0, size * 0.38);
  ctx.quadraticCurveTo(-size * 0.2, size * 0.35, -size * 0.2, size * 0.15);
  ctx.lineTo(-size * 0.2, -size * 0.1);
  ctx.closePath();
  ctx.fill();
}

/**
 * 绘制 '2x' 文字图标（双倍分数）。
 */
function drawDoubleScoreText(ctx: CanvasRenderingContext2D, size: number): void {
  const fontSize = size * 1.1;
  ctx.font = `bold ${fontSize}px "Courier New", monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('2×', 0, 0);
}

/**
 * 绘制向下箭头图标（缩小药剂）。
 */
function drawDownArrow(ctx: CanvasRenderingContext2D, size: number): void {
  // 箭头主体（垂直线）
  ctx.strokeStyle = ctx.fillStyle;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.55);
  ctx.lineTo(0, size * 0.4);
  ctx.stroke();

  // 箭头头部（V 形）
  ctx.beginPath();
  ctx.moveTo(-size * 0.45, size * 0.05);
  ctx.lineTo(0, size * 0.55);
  ctx.lineTo(size * 0.45, size * 0.05);
  ctx.fill();
}

// ============================================================================
// 6. 蛇身绘制
// ============================================================================

/**
 * 绘制蛇身：渐变色段 + 发光效果 + 头部眼睛。
 *
 * 蛇身渲染特点：
 * - 从头部到尾部颜色渐变过渡（基于得分配置）
 * - 每个节段为圆角矩形，带外发光效果
 * - 头部节段绘制两只眼睛（白色眼底 + 黑色瞳孔）
 * - 眼睛方向根据蛇的移动方向动态调整
 *
 * 渐变颜色由 getSnakeGradientColors(score) 确定：
 * - 0-50 分：青绿色系
 * - 51-150 分：紫蓝色系
 * - 151+ 分：金橙色系
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param snake - 蛇实体
 * @param cellSize - 单元格像素大小
 * @param score - 当前得分（决定渐变色系）
 * @param time - 当前时间戳（毫秒），用于微妙的动态效果
 */
export function drawSnake(
  ctx: CanvasRenderingContext2D,
  snake: Snake,
  cellSize: number,
  score: number,
  time: number
): void {
  const { body, direction } = snake;
  const bodyLength = body.length;

  if (bodyLength === 0) {
    return;
  }

  // 获取当前得分对应的渐变色系
  const gradientColors = getSnakeGradientColors(score);

  // 段间间距（略小于 cellSize，使节段之间有微小间隙以增强立体感）
  const segmentSize = cellSize * 0.85;
  const cornerRadius = segmentSize * 0.3;

  ctx.save();

  // 从尾部向头部绘制，确保头部在最上层
  for (let i = bodyLength - 1; i >= 0; i--) {
    const segment = body[i];
    const { px, py } = gridToPixel(segment.x, segment.y, cellSize);

    // 计算当前节段在渐变色中的插值位置
    const t = bodyLength > 1 ? i / (bodyLength - 1) : 0;
    const segmentColor = lerpColor(gradientColors[0], gradientColors[1], t);

    // 发光强度从头到尾递减
    const glowIntensity = 1 - i / bodyLength;
    const shadowBlur = 4 + glowIntensity * 8;

    // --- 绘制蛇身节段 ---
    ctx.fillStyle = segmentColor;
    ctx.shadowColor = segmentColor;
    ctx.shadowBlur = shadowBlur;

    // 圆角矩形
    const halfSize = segmentSize / 2;
    const x = px - halfSize;
    const y = py - halfSize;

    ctx.beginPath();
    ctx.moveTo(x + cornerRadius, y);
    ctx.lineTo(x + segmentSize - cornerRadius, y);
    ctx.quadraticCurveTo(x + segmentSize, y, x + segmentSize, y + cornerRadius);
    ctx.lineTo(x + segmentSize, y + segmentSize - cornerRadius);
    ctx.quadraticCurveTo(
      x + segmentSize,
      y + segmentSize,
      x + segmentSize - cornerRadius,
      y + segmentSize
    );
    ctx.lineTo(x + cornerRadius, y + segmentSize);
    ctx.quadraticCurveTo(x, y + segmentSize, x, y + segmentSize - cornerRadius);
    ctx.lineTo(x, y + cornerRadius);
    ctx.quadraticCurveTo(x, y, x + cornerRadius, y);
    ctx.closePath();
    ctx.fill();

    // --- 头部绘制眼睛（仅头部节段） ---
    if (i === 0) {
      drawSnakeEyes(ctx, px, py, direction, cellSize, time);
    }
  }

  ctx.restore();
}

/**
 * 在蛇头位置绘制眼睛。
 *
 * 眼睛位置根据蛇的移动方向动态调整：
 * - 向上移动：眼睛偏上方
 * - 向下移动：眼睛偏下方
 * - 向左移动：眼睛偏左侧
 * - 向右移动：眼睛偏右侧
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param px - 头部中心 X 像素坐标
 * @param py - 头部中心 Y 像素坐标
 * @param direction - 蛇的移动方向
 * @param cellSize - 单元格像素大小
 * @param time - 当前时间戳（毫秒），用于瞳孔微动
 */
function drawSnakeEyes(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  direction: { x: number; y: number },
  cellSize: number,
  time: number
): void {
  const eyeRadius = cellSize * 0.12;
  const pupilRadius = eyeRadius * 0.55;
  const eyeOffset = cellSize * 0.22; // 眼睛偏离中心的距离

  // 根据方向计算两只眼睛的中心位置
  let leftEyeX: number, leftEyeY: number;
  let rightEyeX: number, rightEyeY: number;

  if (direction.x === 1) {
    // 向右
    leftEyeX = px + eyeOffset * 0.3;
    leftEyeY = py - eyeOffset;
    rightEyeX = px + eyeOffset * 0.3;
    rightEyeY = py + eyeOffset;
  } else if (direction.x === -1) {
    // 向左
    leftEyeX = px - eyeOffset * 0.3;
    leftEyeY = py - eyeOffset;
    rightEyeX = px - eyeOffset * 0.3;
    rightEyeY = py + eyeOffset;
  } else if (direction.y === -1) {
    // 向上
    leftEyeX = px - eyeOffset;
    leftEyeY = py - eyeOffset * 0.3;
    rightEyeX = px + eyeOffset;
    rightEyeY = py - eyeOffset * 0.3;
  } else {
    // 向下
    leftEyeX = px - eyeOffset;
    leftEyeY = py + eyeOffset * 0.3;
    rightEyeX = px + eyeOffset;
    rightEyeY = py + eyeOffset * 0.3;
  }

  // 瞳孔微动效果（基于时间的微小偏移）
  const pupilShift = Math.sin((time / 800) * Math.PI * 2) * 0.5;

  // 绘制左眼
  drawSingleEye(ctx, leftEyeX, leftEyeY, eyeRadius, pupilRadius, direction, pupilShift);

  // 绘制右眼
  drawSingleEye(ctx, rightEyeX, rightEyeY, eyeRadius, pupilRadius, direction, pupilShift);
}

/**
 * 绘制单只眼睛（白色眼底 + 黑色瞳孔）。
 */
function drawSingleEye(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  eyeRadius: number,
  pupilRadius: number,
  direction: { x: number; y: number },
  pupilShift: number
): void {
  ctx.save();

  // 重置阴影避免眼睛发光
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // --- 白色眼底 ---
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(cx, cy, eyeRadius, 0, Math.PI * 2);
  ctx.fill();

  // --- 黑色瞳孔（略微偏向移动方向） ---
  const pupilX = cx + direction.x * pupilShift;
  const pupilY = cy + direction.y * pupilShift;

  ctx.fillStyle = '#000000';
  ctx.beginPath();
  ctx.arc(pupilX, pupilY, pupilRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// ============================================================================
// 7. 障碍物绘制
// ============================================================================

/**
 * 绘制场景中的灰色障碍物。
 *
 * 障碍物为灰色方块，带微弱边框和阴影效果，
 * 视觉上与蛇身、食物、道具明确区分。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param obstacles - 障碍物列表
 * @param cellSize - 单元格像素大小
 */
export function drawObstacles(
  ctx: CanvasRenderingContext2D,
  obstacles: Obstacle[],
  cellSize: number
): void {
  if (obstacles.length === 0) {
    return;
  }

  ctx.save();

  const obstacleSize = cellSize * 0.75;
  const halfSize = obstacleSize / 2;
  const cornerRadius = obstacleSize * 0.15;

  for (const obstacle of obstacles) {
    const { px, py } = gridToPixel(
      obstacle.position.x,
      obstacle.position.y,
      cellSize
    );

    const x = px - halfSize;
    const y = py - halfSize;

    // 阴影效果
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 4;

    // 填充灰色主体
    ctx.fillStyle = COLOR_SCHEME.obstacle;
    ctx.beginPath();
    ctx.moveTo(x + cornerRadius, y);
    ctx.lineTo(x + obstacleSize - cornerRadius, y);
    ctx.quadraticCurveTo(x + obstacleSize, y, x + obstacleSize, y + cornerRadius);
    ctx.lineTo(x + obstacleSize, y + obstacleSize - cornerRadius);
    ctx.quadraticCurveTo(
      x + obstacleSize,
      y + obstacleSize,
      x + obstacleSize - cornerRadius,
      y + obstacleSize
    );
    ctx.lineTo(x + cornerRadius, y + obstacleSize);
    ctx.quadraticCurveTo(x, y + obstacleSize, x, y + obstacleSize - cornerRadius);
    ctx.lineTo(x, y + cornerRadius);
    ctx.quadraticCurveTo(x, y, x + cornerRadius, y);
    ctx.closePath();
    ctx.fill();

    // 边框
    ctx.shadowBlur = 0;
    ctx.strokeStyle = COLOR_SCHEME.obstacleBorder;
    ctx.lineWidth = 1;
    ctx.stroke();

    // 内部 X 标记
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.lineWidth = 1.5;
    const inset = halfSize * 0.4;
    ctx.beginPath();
    ctx.moveTo(px - inset, py - inset);
    ctx.lineTo(px + inset, py + inset);
    ctx.moveTo(px + inset, py - inset);
    ctx.lineTo(px - inset, py + inset);
    ctx.stroke();
  }

  ctx.restore();
}

// ============================================================================
// 8. 粒子特效绘制
// ============================================================================

/**
 * 绘制所有活跃粒子。
 *
 * 每个粒子绘制为一个小圆点，其透明度根据剩余生命比例计算：
 * alpha = currentLife / maxLife
 * 这使得粒子在生命末期逐渐淡出，产生自然的消散效果。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param particles - 活跃粒子数组
 * @param cellSize - 单元格像素大小（用于缩放粒子坐标）
 */
export function drawParticles(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  cellSize: number
): void {
  if (particles.length === 0) {
    return;
  }

  ctx.save();
  // 粒子不需要阴影，避免性能问题
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  for (const particle of particles) {
    // 透明度 = 剩余生命 / 最大生命
    const alpha = Math.max(0, Math.min(1, particle.life / particle.maxLife));

    // 跳过完全透明的粒子
    if (alpha <= 0) {
      continue;
    }

    // 将粒子的网格坐标转换为像素坐标
    const { px, py } = gridToPixel(particle.x, particle.y, cellSize);

        ctx.globalAlpha = alpha;
    ctx.fillStyle = particle.color;
    ctx.beginPath();
    // 粒子尺寸放大4倍，与画面缩放保持一致
    ctx.arc(px, py, particle.size * 4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

// ============================================================================
// 9. 游戏结束特效绘制
// ============================================================================

/**
 * 绘制游戏结束状态的特化粒子效果。
 *
 * 与 drawParticles 的区别：
 * - 粒子尺寸放大 1.5 倍，增强视觉冲击力
 * - 启用发光效果，营造蛇身碎裂的戏剧性
 * - 整体透明度额外降低 20%，营造残影感
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param particles - 游戏结束粒子数组
 * @param cellSize - 单元格像素大小
 */
export function drawGameOverEffect(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  cellSize: number
): void {
  if (particles.length === 0) {
    return;
  }

  ctx.save();

  for (const particle of particles) {
    const alpha = Math.max(0, Math.min(1, particle.life / particle.maxLife)) * 0.8;
    if (alpha <= 0) {
      continue;
    }

    const { px, py } = gridToPixel(particle.x, particle.y, cellSize);

    ctx.globalAlpha = alpha;
    ctx.fillStyle = particle.color;
    ctx.shadowColor = particle.color;
    ctx.shadowBlur = 6;

        // 放大粒子尺寸：基础1.5倍游戏结束特效放大 × 4倍画面缩放
    const scaledSize = particle.size * 1.5 * 4;
    ctx.beginPath();
    ctx.arc(px, py, scaledSize, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

// ============================================================================
// 10. 主渲染编排函数
// ============================================================================

/**
 * 编排并执行完整的游戏帧渲染。
 *
 * 渲染顺序严格遵循设计规范：
 * 1. 清空画布（填充背景色）
 * 2. 绘制背景网格线
 * 3. 绘制墙壁霓虹边框
 * 4. 绘制食物（含脉冲动画）
 * 5. 绘制道具（含浮动和旋转动画）
 * 6. 绘制蛇身（渐变色 + 发光 + 眼睛）
 * 7. 绘制粒子特效
 *
 * 此函数是渲染模块的对外主入口，调用方只需传入完整的游戏状态即可完成一帧渲染。
 *
 * @param ctx - Canvas 2D 渲染上下文
 * @param gameState - 完整游戏状态（包含所有实体和配置）
 * @param cellSize - 单元格像素大小
 * @param time - 当前时间戳（毫秒），用于驱动所有动画
 */
export function renderGame(
  ctx: CanvasRenderingContext2D,
  gameState: {
    grid: GridConfig;
    food: Food;
    powerUpItem: PowerUpItem | null;
    snake: Snake;
    score: { current: number };
    particles: Particle[];
    obstacles: Obstacle[];
    status: string;
  },
  cellSize: number,
  time: number
): void {
  const { grid, food, powerUpItem, snake, score, particles, obstacles, status } = gameState;
  const totalWidth = grid.width * cellSize;
  const totalHeight = grid.height * cellSize;

  // 1. 清空画布
  clearCanvas(ctx, totalWidth, totalHeight);

  // 2. 绘制背景网格
  drawGrid(ctx, grid, cellSize);

  // 3. 绘制墙壁霓虹边框
  drawWallBorder(ctx, grid, cellSize, time);

  // 4. 绘制障碍物（在食物和道具下方）
  drawObstacles(ctx, obstacles, cellSize);

  // 5. 绘制食物
  drawFood(ctx, food, cellSize, time);

  // 6. 绘制道具
  drawPowerUp(ctx, powerUpItem, cellSize, time);

  // 7. 绘制蛇身
  drawSnake(ctx, snake, cellSize, score.current, time);

  // 8. 绘制粒子特效（游戏结束状态使用特化渲染）
  if (status === 'gameover') {
    drawGameOverEffect(ctx, particles, cellSize);
  } else {
    drawParticles(ctx, particles, cellSize);
  }
}

// ============================================================================
// 11. 单元格尺寸计算
// ============================================================================

/**
 * 计算最优单元格像素大小，使网格适应容器尺寸同时保持宽高比。
 *
 * 计算策略：
 * 1. 分别计算宽度和高度方向上的最大可能 cellSize：
 *    - 宽度方向：cellSizeX = containerWidth / gridWidth
 *    - 高度方向：cellSizeY = containerHeight / gridHeight
 * 2. 取两者中的较小值，确保网格不超出容器
 * 3. 向下取整为整数像素，避免亚像素渲染导致的模糊
 * 4. 最小值为 4 像素，确保在极小屏幕上仍可玩
 *
 * @param grid - 网格配置（宽度、高度列数）
 * @param containerWidth - 可用容器宽度（像素）
 * @param containerHeight - 可用容器高度（像素）
 * @returns 计算得到的最优单元格大小（整数像素）
 *
 * @example
 * const grid = { width: 20, height: 20, cellSize: 0 };
 * calculateCellSize(grid, 600, 600); // => 30
 * calculateCellSize(grid, 300, 600); // => 15（受宽度限制）
 */
export function calculateCellSize(
  grid: GridConfig,
  containerWidth: number,
  containerHeight: number
): number {
  // 防止除零或负值
  if (containerWidth <= 0 || containerHeight <= 0) {
    return 4;
  }

  const cellSizeX = Math.floor(containerWidth / grid.width);
  const cellSizeY = Math.floor(containerHeight / grid.height);

  // 取较小值确保网格完全适配容器
  const cellSize = Math.min(cellSizeX, cellSizeY);

    // 最小 cellSize 限制，确保可玩性
  // 放大4倍以满足用户需求
  return Math.max(4, cellSize) * 4;
}